import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * ActionMenu - kebab-style dropdown for table row actions.
 *
 * Usage:
 *   <ActionMenu items={[
 *     { label: 'Edit', icon: <EditIcon />, onClick: () => {} },
 *     { label: 'Delete', variant: 'danger', onClick: () => {} },
 *     { label: 'Copy Link', onClick: () => {}, disabled: true },
 *   ]} />
 *
 * Item props:
 *   label    - string (required)
 *   onClick  - function
 *   icon     - JSX element (optional)
 *   variant  - 'default' | 'danger'  (default: 'default')
 *   disabled - boolean
 */

const DROPDOWN_WIDTH = 176; // w-44

const ActionMenu = ({ items = [] }) => {
   const [open, setOpen] = useState(false);
   const [coords, setCoords] = useState({ top: 0, left: 0 });
   const triggerRef = useRef(null);
   const dropdownRef = useRef(null);

   // Close on outside click or scroll
   useEffect(() => {
      if (!open) return;
      const close = (e) => {
         if (
            dropdownRef.current &&
            !dropdownRef.current.contains(e.target) &&
            triggerRef.current &&
            !triggerRef.current.contains(e.target)
         ) {
            setOpen(false);
         }
      };
      const closeOnScroll = () => setOpen(false);
      document.addEventListener('mousedown', close);
      document.addEventListener('scroll', closeOnScroll, true);
      return () => {
         document.removeEventListener('mousedown', close);
         document.removeEventListener('scroll', closeOnScroll, true);
      };
   }, [open]);

   const handleOpen = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = items.length * 36 + 8; // approx height
      const openUpward = spaceBelow < dropdownHeight;

      setCoords({
         top: openUpward ? rect.top - dropdownHeight : rect.bottom + 4,
         left: Math.min(rect.right - DROPDOWN_WIDTH, window.innerWidth - DROPDOWN_WIDTH - 8),
      });
      setOpen((prev) => !prev);
   };

   const handleItemClick = (item) => {
      if (item.disabled) return;
      setOpen(false);
      item.onClick?.();
   };

   return (
      <>
         {/* Trigger */}
         <button
            ref={triggerRef}
            type="button"
            onClick={handleOpen}
            className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none"
            aria-label="Actions"
         >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
               <circle cx="12" cy="5" r="1.5" />
               <circle cx="12" cy="12" r="1.5" />
               <circle cx="12" cy="19" r="1.5" />
            </svg>
         </button>

         {/* Dropdown - rendered in document.body via portal to escape overflow:hidden */}
         {open &&
            createPortal(
               <div
                  ref={dropdownRef}
                  style={{ top: coords.top, left: coords.left, width: DROPDOWN_WIDTH }}
                  className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg py-1"
               >
                  {items.map((item, i) => {
                     const isDanger = item.variant === 'danger';
                     const isDisabled = item.disabled;

                     return (
                        <button
                           key={i}
                           type="button"
                           onClick={() => handleItemClick(item)}
                           disabled={isDisabled}
                           className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors text-left
                           ${
                              isDisabled
                                 ? 'opacity-40 cursor-not-allowed text-gray-500 dark:text-gray-400'
                                 : isDanger
                                   ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                                   : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                           }`}
                        >
                           {item.icon && (
                              <span className="w-4 h-4 flex items-center justify-center shrink-0">
                                 {item.icon}
                              </span>
                           )}
                           {item.label}
                        </button>
                     );
                  })}
               </div>,
               document.body
            )}
      </>
   );
};

export default ActionMenu;
